import React, { lazy, Suspense } from 'react';
import { Phone, Mail, BookOpen, Code, ChevronRight, Facebook, Linkedin, ArrowRight, Youtube, MessageCircle, Instagram, Send } from 'lucide-react';
import { Link } from 'react-router-dom';
import { XLogo } from './components/XLogo';
import { TTRGLogo } from './components/TTRGLogo';

// Lazy load the BookingSystem component with explicit chunk name
const BookingSystem = lazy(() => import(/* webpackChunkName: "booking-system" */ './components/BookingSystem'));

const services = [
  {
    icon: TTRGLogo,
    title: 'AI Chatbots',
    description: 'Intelligent sales & support chatbots that engage customers 24/7',
    link: '/chatbot'
  },
  {
    icon: Phone,
    title: 'AI Voice Agents',
    description: 'Advanced phone-based customer care solutions',
    link: '/voice-agent'
  },
  {
    icon: Mail,
    title: 'Automation Templates',
    description: 'Streamlined email and CRM automation workflows',
    link: '/email-automation'
  },
  {
    icon: BookOpen,
    title: 'Training & Workshop',
    description: 'Learn AI fundamentals and implementation strategies',
    link: '/training'
  },
  {
    icon: Code,
    title: 'Micro SaaS Development',
    description: 'Custom AI-powered software solutions for your business',
    link: '/micro-saas'
  }
];

const testimonials = [
  {
    quote: "TTRG's AI chatbot increased our lead generation by 50% in just two months.",
    author: "Sarah Chen",
    role: "Marketing Director"
  },
  {
    quote: "The automation templates saved our team countless hours of manual work.",
    author: "Michael Lee",
    role: "Operations Manager"
  },
  {
    quote: "Their AI voice agents transformed our customer service capabilities.",
    author: "David Wong",
    role: "Customer Success Lead"
  }
];

const RunningText = () => (
  <div className="bg-primary text-white py-2 overflow-hidden whitespace-nowrap">
    <div className="animate-marquee inline-block">
      Faceless Youtube Video Creation • Faceless Youtube Video Creation • Faceless Youtube Video Creation •&nbsp;
    </div>
  </div>
);

function App() {
  return (
    <div className="min-h-screen bg-background">
      <RunningText />
      {/* Header */}
      <header className="sticky top-0 z-50 shadow-sm bg-white/95 backdrop-blur-sm">
        <nav className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between relative">
          <div className="flex items-center space-x-2 font-bold text-xl text-primary">
            <TTRGLogo className="w-8 h-8" />
            <span>TTRG</span>
          </div>
          <div className="hidden md:flex items-center space-x-4">
            <a href="#services" className="text-text hover:text-accent transition-colors">Services</a>
            <a href="#pricing" className="text-text hover:text-accent transition-colors">Pricing</a>
            <Link to="/blog" className="text-text hover:text-accent transition-colors">Blog</Link>
            <a href="#testimonials" className="text-text hover:text-accent transition-colors">Testimonials</a>
            <a href="#contact" className="text-text hover:text-accent transition-colors">Contact</a>
            <Link 
              to="/login"
              className="bg-black text-white px-3 py-1.5 rounded-lg hover:bg-gray-800 transition-colors text-sm"
            >
              Login
            </Link>
            <Link 
              to="/register"
              className="bg-black text-white px-3 py-1.5 rounded-lg hover:bg-gray-800 transition-colors text-sm"
            >
              Register
            </Link>
            <a 
              href="#book-demo"
              className="bg-primary text-white px-4 py-2 rounded-lg transition hover:opacity-90 hover:bg-accent"
            >
              Book a Demo
            </a>
          </div>
          <button 
            className="md:hidden text-gray-600 hover:text-indigo-600"
            onClick={() => document.getElementById('mobile-menu')?.classList.toggle('hidden')}
            aria-label="Toggle menu"
          >
            <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
            </svg>
          </button>
          <div id="mobile-menu" className="hidden absolute top-full left-0 right-0 bg-white shadow-lg rounded-b-lg p-4 md:hidden">
            <div className="flex flex-col space-y-4">
              <a href="#services" className="text-gray-600 hover:text-indigo-600">Services</a>
              <a href="#pricing" className="text-gray-600 hover:text-indigo-600">Pricing</a>
              <Link to="/blog" className="text-gray-600 hover:text-indigo-600">Blog</Link>
              <a href="#testimonials" className="text-gray-600 hover:text-indigo-600">Testimonials</a>
              <a href="#contact" className="text-gray-600 hover:text-indigo-600">Contact</a>
              <Link 
                to="/login"
                className="bg-black text-white px-3 py-1.5 rounded-lg text-center hover:bg-gray-800 transition-colors text-sm"
              >
                Login
              </Link>
              <Link 
                to="/register"
                className="bg-black text-white px-3 py-1.5 rounded-lg text-center hover:bg-gray-800 transition-colors text-sm"
              >
                Register
              </Link>
              <a 
                href="#book-demo"
                className="bg-indigo-600 text-white px-4 py-2 rounded-lg hover:bg-indigo-700 transition text-center"
              >
                Book a Demo
              </a>
            </div>
          </div>
        </nav>
      </header>

      {/* Hero Section */}
      <section className="py-20 px-4">
        <div className="max-w-7xl mx-auto text-center">
          <h1 className="text-4xl md:text-5xl font-bold text-primary mb-6">
            Transform Your Business with AI
          </h1>
          <p className="text-lg md:text-xl text-text mb-12 max-w-3xl mx-auto">
            Leverage cutting-edge AI solutions to automate customer interactions, streamline operations, and drive growth.
          </p>
          <div className="flex flex-col sm:flex-row justify-center gap-4">
            <a 
              href="#book-demo"
              className="bg-primary text-white px-8 py-3 rounded-lg text-lg font-semibold hover:bg-accent transition-colors inline-flex items-center justify-center gap-2"
            >
              Book a Demo <ArrowRight className="w-5 h-5" />
            </a>
            <a 
              href="#services"
              className="border-2 border-primary text-primary px-8 py-3 rounded-lg text-lg font-semibold hover:bg-primary hover:text-white transition-colors"
            >
              Learn More
            </a>
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section id="services" className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4">
          <h2 className="text-3xl md:text-4xl font-bold text-primary text-center mb-16">Our Services</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {services.map((service, index) => (
              <div 
                key={index} 
                className="bg-white p-6 rounded-lg shadow-md hover:shadow-lg transition-shadow transform hover:-translate-y-1 duration-200"
              >
                <service.icon className="w-12 h-12 text-primary mb-4" />
                <h3 className="text-xl font-semibold mb-2">{service.title}</h3>
                <p className="text-text mb-4">{service.description}</p>
                <Link 
                  to={service.link}
                  className="text-primary font-semibold inline-flex items-center gap-1 hover:text-accent transition-colors"
                >
                  Learn more <ChevronRight className="w-4 h-4" />
                </Link>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Booking System Section */}
      <section id="book-demo" className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4">
          <h2 className="text-3xl md:text-4xl font-bold text-primary text-center mb-6">Book a Demo</h2>
          <p className="text-lg md:text-xl text-gray-600 text-center mb-12 max-w-2xl mx-auto">
            Schedule a 15-minute demo to see how our AI solutions can transform your business
          </p>
          <Suspense fallback={
            <div className="flex justify-center items-center min-h-[200px]">
              <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary"></div>
            </div>
          }>
            <BookingSystem />
          </Suspense>
        </div>
      </section>

      {/* Testimonials Section */}
      <section id="testimonials" className="py-20">
        <div className="max-w-7xl mx-auto px-4">
          <h2 className="text-4xl font-bold text-primary text-center mb-16">What Our Clients Say</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {testimonials.map((testimonial, index) => (
              <div key={index} className="bg-white p-6 rounded-lg shadow-md">
                <p className="text-lg mb-4 italic">"{testimonial.quote}"</p>
                <div>
                  <p className="font-semibold">{testimonial.author}</p>
                  <p className="text-text">{testimonial.role}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section id="contact" className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4">
          <h2 className="text-4xl font-bold text-primary text-center mb-16">Get in Touch</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-12">
            <div>
              <h3 className="text-2xl font-semibold mb-6">Contact Information</h3>
              <div className="space-y-4">
                <a 
                  href="https://wa.me/message/7NHLFKTLV266O1"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-3 text-text hover:text-primary transition-colors"
                >
                  <MessageCircle className="w-6 h-6" />
                  <span>WhatsApp Business</span>
                </a>
                <a 
                  href="mailto:contact@ttrg.io"
                  className="flex items-center gap-3 text-text hover:text-primary transition-colors"
                >
                  <Mail className="w-6 h-6" />
                  <span>contact@ttrg.io</span>
                </a>
              </div>
              <div className="mt-8">
                <h4 className="text-xl font-semibold mb-4">Follow Us</h4>
                <div className="flex gap-4">
                  <a 
                    href="https://facebook.com/ttrgai"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-text hover:text-primary transition-colors"
                  >
                    <Facebook className="w-6 h-6" />
                  </a>
                  <a 
                    href="https://twitter.com/ttrgai"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-text hover:text-primary transition-colors"
                  >
                    <XLogo className="w-6 h-6" />
                  </a>
                  <a 
                    href="https://linkedin.com/company/ttrgai"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-text hover:text-primary transition-colors"
                  >
                    <Linkedin className="w-6 h-6" />
                  </a>
                  <a 
                    href="https://youtube.com/@ttrgai"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-text hover:text-primary transition-colors"
                  >
                    <Youtube className="w-6 h-6" />
                  </a>
                  <a 
                    href="https://instagram.com/ttrgai"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-text hover:text-primary transition-colors"
                  >
                    <Instagram className="w-6 h-6" />
                  </a>
                </div>
              </div>
            </div>
            <div>
              <form className="space-y-6">
                <div>
                  <label htmlFor="name" className="block text-sm font-medium text-text mb-1">
                    Name
                  </label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-primary"
                    required
                  />
                </div>
                <div>
                  <label htmlFor="email" className="block text-sm font-medium text-text mb-1">
                    Email
                  </label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-primary"
                    required
                  />
                </div>
                <div>
                  <label htmlFor="message" className="block text-sm font-medium text-text mb-1">
                    Message
                  </label>
                  <textarea
                    id="message"
                    name="message"
                    rows={4}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-primary"
                    required
                  ></textarea>
                </div>
                <button
                  type="submit"
                  className="w-full bg-primary text-white px-6 py-3 rounded-lg font-semibold hover:bg-accent transition-colors flex items-center justify-center gap-2"
                >
                  Send Message <Send className="w-5 h-5" />
                </button>
              </form>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-primary text-white py-12">
        <div className="max-w-7xl mx-auto px-4">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
            <div>
              <div className="flex items-center space-x-2 font-bold text-xl mb-4">
                <TTRGLogo className="w-8 h-8 text-white" />
                <span>TTRG</span>
              </div>
              <p className="text-gray-300">
                Empowering businesses with intelligent AI solutions.
              </p>
            </div>
            <div>
              <h4 className="font-semibold text-lg mb-4">Services</h4>
              <ul className="space-y-2">
                <li><Link to="/chatbot" className="text-gray-300 hover:text-white transition-colors">AI Chatbots</Link></li>
                <li><Link to="/voice-agent" className="text-gray-300 hover:text-white transition-colors">AI Voice Agents</Link></li>
                <li><Link to="/email-automation" className="text-gray-300 hover:text-white transition-colors">Automation Templates</Link></li>
                <li><Link to="/training" className="text-gray-300 hover:text-white transition-colors">Training & Workshop</Link></li>
                <li><Link to="/micro-saas" className="text-gray-300 hover:text-white transition-colors">Micro SaaS Development</Link></li>
              </ul>
            </div>
            <div>
              <h4 className="font-semibold text-lg mb-4">Company</h4>
              <ul className="space-y-2">
                <li><Link to="/about" className="text-gray-300 hover:text-white transition-colors">About Us</Link></li>
                <li><Link to="/blog" className="text-gray-300 hover:text-white transition-colors">Blog</Link></li>
                <li><Link to="/careers" className="text-gray-300 hover:text-white transition-colors">Careers</Link></li>
                <li><Link to="/privacy" className="text-gray-300 hover:text-white transition-colors">Privacy Policy</Link></li>
                <li><Link to="/terms" className="text-gray-300 hover:text-white transition-colors">Terms of Service</Link></li>
              </ul>
            </div>
            <div>
              <h4 className="font-semibold text-lg mb-4">Newsletter</h4>
              <p className="text-gray-300 mb-4">Subscribe to our newsletter for the latest updates.</p>
              <form className="flex gap-2">
                <input
                  type="email"
                  placeholder="Enter your email"
                  className="flex-1 px-4 py-2 rounded-lg text-gray-900"
                />
                <button
                  type="submit"
                  className="bg-white text-primary px-4 py-2 rounded-lg hover:bg-gray-100 transition-colors"
                >
                  Subscribe
                </button>
              </form>
            </div>
          </div>
          <div className="mt-12 pt-8 border-t border-gray-700 text-center text-gray-300">
            <p>&copy; {new Date().getFullYear()} TTRG. All rights reserved.</p>
          </div>
        </div>
      </footer>
    </div>
  );
}

export default App;